// src/api/axiosClient.js
// -----------------------------------------------------------------------------
// Single shared Axios instance used by EVERY service file in src/api/.
// Keeping one instance here means:
//   1. Base URL is defined in ONE place (reads from .env -> VITE_API_BASE_URL)
//   2. The JWT auth token is attached to every request automatically
//   3. 401 (token expired) responses are handled in ONE place (auto logout)
// Every *Service.js file in this folder should import "axiosClient" instead of
// importing axios directly, and call axiosClient.get/post/put/delete(...).
// -----------------------------------------------------------------------------
import axios from 'axios';
import loadingStore from './loadingStore';

// Base URL of the AFix C# Web API. Falls back to "/api" (proxied by Vite in dev).
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30s timeout for slow requests
});

// ---------------------------------------------------------------------------
// REQUEST INTERCEPTOR
// Attaches the JWT access token (stored after login) to every outgoing
// request, matching the backend's "Secure login using JWT authentication"
// requirement (Original Requirement #2).
// ---------------------------------------------------------------------------
axiosClient.interceptors.request.use(
  (config) => {
    // Increment loading count if the loader is not explicitly bypassed
    if (!config.skipGlobalLoader) {
      loadingStore.increment();
    }
    const token = localStorage.getItem('afix_access_token');
    if (token && token !== 'undefined' && token !== 'null') {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    if (error?.config && !error.config.skipGlobalLoader) {
      loadingStore.decrement();
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// RESPONSE INTERCEPTOR
// - On 401 Unauthorized: token is invalid/expired -> clear storage & redirect
//   to login. (Refresh-token handling can be added here later.)
// - On any other error: just pass it along so the calling component/page can
//   show a toast / inline error message.
// ---------------------------------------------------------------------------
axiosClient.interceptors.response.use(
  (response) => {
    if (!response.config?.skipGlobalLoader) {
      loadingStore.decrement();
    }
    return response;
  },
  (error) => {
    if (!error.config?.skipGlobalLoader) {
      loadingStore.decrement();
    }
    if (error?.response?.status === 401) {
      const token = localStorage.getItem('afix_access_token');
      if (token && token !== 'demo-token') {
        console.warn("HTTP 401 Unauthorized: Token expired or invalid. Logging out...");
        localStorage.removeItem('afix_access_token');
        localStorage.removeItem('afix_refresh_token');
        localStorage.removeItem('afix_user');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export default axiosClient;
